import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Buildings, Users, MapPin, CalendarBlank, Warning, CheckCircle,
  XCircle, Clock, Crown, ArrowRight, Gauge
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const CHART_COLORS = ['#0EA5E9', '#8B5CF6', '#10B981', '#F59E0B', '#F43F5E', '#06B6D4'];
const PLAN_COLORS = { basic: '#94A3B8', standard: '#0EA5E9', professional: '#8B5CF6', enterprise: '#F59E0B', trial: '#F97316' };

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await adminApi.dashboard();
        setData(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
    </div>
  );

  if (!data) return <p className="text-slate-500 text-center py-20">Failed to load dashboard</p>;

  const statCards = [
    { title: 'Total Hospitals', value: data.total_hospitals, icon: Buildings, color: 'from-sky-500 to-blue-600', iconBg: 'bg-sky-50', iconColor: 'text-sky-600' },
    { title: 'Active', value: data.active_hospitals, icon: CheckCircle, color: 'from-emerald-500 to-green-600', iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    { title: 'Inactive', value: data.inactive_hospitals, icon: XCircle, color: 'from-slate-400 to-slate-600', iconBg: 'bg-slate-50', iconColor: 'text-slate-500' },
    { title: 'Nearing Expiry', value: data.hospitals_nearing_expiry, icon: Warning, color: 'from-amber-500 to-orange-500', iconBg: 'bg-amber-50', iconColor: 'text-amber-600' },
  ];

  const planData = (data.plan_distribution || []).map(p => ({
    name: (p.plan || 'basic').charAt(0).toUpperCase() + (p.plan || 'basic').slice(1),
    value: p.count,
    fill: PLAN_COLORS[p.plan] || '#94A3B8'
  }));

  const onboardingData = (data.onboarding_distribution || []).map(o => ({
    name: (o.status || 'pending').charAt(0).toUpperCase() + (o.status || 'pending').slice(1),
    value: o.count,
  }));

  return (
    <div className="space-y-6" data-testid="super-admin-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 font-['Outfit']" data-testid="admin-dashboard-title">
            Hospital Account Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">Platform overview - Hospital accounts & subscriptions</p>
        </div>
        <Badge className="bg-violet-100 text-violet-700 border-0 text-xs gap-1 px-3 py-1">
          <Crown className="h-3.5 w-3.5" /> Super Admin
        </Badge>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stat-cards">
        {statCards.map((card, i) => (
          <Card key={i} className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`stat-card-${i}`}>
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{card.title}</p>
                  <p className="text-3xl font-bold text-slate-800 mt-1 font-['Outfit']">{card.value}</p>
                </div>
                <div className={`h-12 w-12 rounded-xl ${card.iconBg} flex items-center justify-center`}>
                  <card.icon className={`h-6 w-6 ${card.iconColor}`} weight="duotone" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Plan Distribution */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 font-['Outfit']">Plan Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {planData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={planData} cx="50%" cy="50%" outerRadius={80} innerRadius={45} dataKey="value" paddingAngle={3} label={({ name, value }) => `${name}: ${value}`}>
                    {planData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-400 text-sm">No plan data yet</div>
            )}
          </CardContent>
        </Card>

        {/* Onboarding Status */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 font-['Outfit']">Onboarding Status</CardTitle>
          </CardHeader>
          <CardContent>
            {onboardingData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={onboardingData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748B' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748B' }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-400 text-sm">No onboarding data</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recently Registered */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Clock className="h-4 w-4" />
        <span>New hospitals this week: <strong className="text-slate-700">{data.recent_activity?.new_hospitals_week || 0}</strong></span>
      </div>

      {/* Hospital Account Table */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="hospital-accounts-table">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold text-slate-700 font-['Outfit']">Hospital Accounts</CardTitle>
          <button onClick={() => navigate('/tenants')} className="text-xs text-sky-600 hover:text-sky-700 flex items-center gap-1 font-medium" data-testid="manage-hospitals-btn">
            Manage <ArrowRight className="h-3 w-3" />
          </button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Hospital</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Plan</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Users</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Branches</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Plan End</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Onboarding</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.hospitals || []).map((h) => (
                  <TableRow key={h.id} className="hover:bg-sky-50/50 transition-colors" data-testid={`hospital-account-${h.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">{h.name}</p>
                        <p className="text-xs text-slate-400">{h.code} {h.city ? `| ${h.city}` : ''}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`border-0 text-xs`} style={{ backgroundColor: `${PLAN_COLORS[h.plan] || '#94A3B8'}20`, color: PLAN_COLORS[h.plan] || '#94A3B8' }}>
                        {(h.plan || 'basic').charAt(0).toUpperCase() + (h.plan || 'basic').slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      <span className="font-medium text-slate-700">{h.users_used}</span>
                      <span className="text-slate-400">/{h.users_allocated}</span>
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      <span className="font-medium text-slate-700">{h.branches_used}</span>
                      <span className="text-slate-400">/{h.branches_allocated}</span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {h.plan_end_date ? (
                        <span className={h.is_nearing_expiry ? 'text-red-600 font-medium' : 'text-slate-600'}>
                          {new Date(h.plan_end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          {h.is_nearing_expiry && <Warning className="inline h-3.5 w-3.5 ml-1 text-red-500" />}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={`border-0 text-xs ${
                        h.onboarding_status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        h.onboarding_status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {(h.onboarding_status || 'pending').replace('_', ' ').charAt(0).toUpperCase() + (h.onboarding_status || 'pending').replace('_', ' ').slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`border-0 text-xs ${h.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {h.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {(data.hospitals || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-400">
                      <Buildings className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>No hospitals registered yet</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminDashboard;
